// MyProfile.js
import { useState, useEffect } from "react";
import { FaUpload, FaCheck, FaTimes, FaInfoCircle, FaEdit, FaUser, FaPhone, FaMapMarkerAlt, FaEnvelope, FaIdCard } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "./Navbar";
import Footer from "./Footer";

/* ─────────────────────────────────────────────────────
   TOAST
───────────────────────────────────────────────────── */
function Toast({ message, type, onClose }) {
  const { t } = useTranslation();
  useEffect(() => {
    const timer = setTimeout(onClose, 5000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const config = {
    success: { icon: <FaCheck />, label: t("toastBlessings"), accentColor: "#2a6b2a", accentLight: "rgba(42,107,42,0.15)", iconBg: "rgba(42,107,42,0.12)", borderColor: "rgba(42,107,42,0.3)", topBarColor: "#2a6b2a", symbol: "✦" },
    error:   { icon: <FaTimes />, label: t("toastError"),     accentColor: "#8b1a1a", accentLight: "rgba(139,26,26,0.12)", iconBg: "rgba(139,26,26,0.1)",   borderColor: "rgba(139,26,26,0.28)",  topBarColor: "#8b1a1a", symbol: "✗" },
    info:    { icon: <FaInfoCircle />, label: t("toastDivineNotice"), accentColor: "#8a6020", accentLight: "rgba(201,151,58,0.1)", iconBg: "rgba(201,151,58,0.1)", borderColor: "rgba(201,151,58,0.3)", topBarColor: "#c9973a", symbol: "ॐ" }
  };
  const c = config[type] || config.info;
  return (
    <div className="toast-container">
      <div className="toast-card" style={{ "--accent": c.accentColor, "--accent-light": c.accentLight, "--border-color": c.borderColor, "--top-bar": c.topBarColor }}>
        <div className="toast-top-bar" />
        <span className="toast-corner tl" /><span className="toast-corner tr" />
        <span className="toast-corner bl" /><span className="toast-corner br" />
        <div className="toast-medallion" style={{ background: c.iconBg, borderColor: c.borderColor }}>
          <div className="toast-medallion-ring" />
          <div className="toast-icon-inner" style={{ color: c.accentColor }}>{c.icon}</div>
        </div>
        <div className="toast-content">
          <div className="toast-label-row">
            <span className="toast-symbol" style={{ color: c.accentColor }}>{c.symbol}</span>
            <h4 className="toast-title" style={{ color: c.accentColor }}>{c.label}</h4>
            <span className="toast-symbol" style={{ color: c.accentColor }}>{c.symbol}</span>
          </div>
          <div className="toast-rule" style={{ background: `linear-gradient(90deg, transparent, ${c.accentColor}55, transparent)` }} />
          <p className="toast-message">{message}</p>
        </div>
        <button className="toast-close" onClick={onClose} style={{ color: c.accentColor }}>✕</button>
        <div className="toast-bottom-rule" />
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────
   ILLUSTRATOR PANEL (reused from Register)
───────────────────────────────────────────────────── */
function IllustratorPanel({ illusSymbol }) {
  const { t } = useTranslation();
  return (
    <div className="illus-panel">
      <div className="illus-rings">
        {[280, 230, 185, 145, 110].map((s, i) => (
          <div key={s} className="illus-ring" style={{ width: s, height: s, animationDelay: `${i * 0.4}s` }} />
        ))}
      </div>
      <div className="illus-medallion">
        <div className="medallion-outer-ring" />
        <div className="medallion-inner">
          <div className={`medallion-face ${illusSymbol === "om" ? "visible" : ""}`}>
            <span className="illus-om">ॐ</span>
          </div>
          <div className={`medallion-face ${illusSymbol === "logo" ? "visible" : ""}`}>
            <img src="./logo.png" alt="Temple Logo" className="illus-logo-img" />
          </div>
        </div>
        <div className="medallion-halo" />
      </div>
      <div className="illus-info">
        <div className="illus-rule" />
        <div className="illus-name">{t("ftTempleName")}</div>
        <div className="illus-sub">{t("ftTempleSub")}</div>
        <div className="illus-loc">✦ {t("templeLoc")} ✦</div>
        <div className="illus-rule" />
      </div>
      <div className="illus-quote"><p>{t("hpQuote")}</p></div>
      <div className="illus-dots">
        {[...Array(5)].map((_, i) => (
          <span key={i} className="illus-dot" style={{ opacity: i === 2 ? 0.7 : 0.25 }} />
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────
   PAGE SHELL
───────────────────────────────────────────────────── */
function PageShell({ children, illusSymbol }) {
  return (
    <div className="login-page">
      <div className="outer-card">
        <div className="outer-card-strip" />
        <div className="login-split">
          <div className="login-left"><IllustratorPanel illusSymbol={illusSymbol} /></div>
          <div className="login-right">{children}</div>
        </div>
        <div className="outer-card-strip bottom" />
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────
   PROFILE VIEW — Grand Temple Manuscript Design
───────────────────────────────────────────────────── */
/* ─────────────────────────────────────────────────────
   ID PROOF MODAL
───────────────────────────────────────────────────── */
function IdProofModal({ src, onClose }) {
  const { t } = useTranslation();
  const isPdf = src && src.toLowerCase().endsWith(".pdf");

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="idm-overlay" onClick={onClose}>
      <div className="idm-dialog" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="idm-header">
          <div className="idm-header-left">
            <div className="idm-header-icon"><FaIdCard /></div>
            <div>
              <span className="idm-eyebrow">✦ {t("lblIdProof")} ✦</span>
              <p className="idm-title">{t("viewIdProof")}</p>
            </div>
          </div>
          <button className="idm-close" onClick={onClose}>
            <FaTimes />
          </button>
        </div>
        <div className="idm-rule" />

        {/* Content */}
        <div className="idm-body">
          {isPdf ? (
            <iframe
              src={src}
              className="idm-iframe"
              title="ID Proof"
            />
          ) : (
            <img src={src} alt="ID Proof" className="idm-img" />
          )}
        </div>

        {/* Footer */}
        <div className="idm-footer">
          <a href={src} target="_blank" rel="noreferrer" className="idm-open-btn">
            <span>{t("viewIdProof")} ↗</span>
          </a>
          <button className="idm-cancel-btn" onClick={onClose}>
            {t("cancel") || "Close"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ProfileView({ profile, onEdit }) {
  const { t } = useTranslation();
  const [showIdModal, setShowIdModal] = useState(false);
  const initial = profile.name ? profile.name.charAt(0).toUpperCase() : "D";
  const fields = [
    { icon: <FaPhone />,        label: t("lblMobile"),  value: `+91 ${profile.mobile_number}`, mono: true },
    { icon: <FaEnvelope />,     label: t("lblEmail"),   value: profile.email },
    { icon: <FaMapMarkerAlt />, label: t("lblPlace"),   value: profile.place },
    { icon: <FaIdCard />,       label: t("lblAddress"), value: profile.address },
  ];

  return (
    <div className="pv-page">
      {/* Ambient background particles */}
      <div className="pv-ambient">
        {[...Array(12)].map((_, i) => (
          <div key={i} className="pv-particle" style={{
            left: `${8 + i * 8}%`,
            animationDelay: `${i * 0.7}s`,
            animationDuration: `${6 + (i % 4) * 2}s`
          }} />
        ))}
      </div>

      <div className="pv-manuscript">
        {/* ── TOP CROWN BAR ── */}
        <div className="pv-crown-bar">
          <div className="pv-crown-inner">
            <div className="pv-crown-wing left" />
            <div className="pv-crown-center">
              <span className="pv-crown-om">ॐ</span>
              <div className="pv-crown-diamonds">
                {[...Array(5)].map((_, i) => <span key={i} className="pv-cdiamond">◆</span>)}
              </div>
            </div>
            <div className="pv-crown-wing right" />
          </div>
        </div>

        {/* ── MAIN BODY ── */}
        <div className="pv-body">

          {/* ══ LEFT SANCTUM ══ */}
          <div className="pv-sanctum">
            {/* Mandala background */}
            <div className="pv-mandala">
              {[240, 200, 160, 124, 92].map((s, i) => (
                <div key={s} className={`pv-mandala-ring pv-mr-${i}`} style={{ width: s, height: s }} />
              ))}
              <div className="pv-mandala-core" />
            </div>

            {/* Avatar */}
            <div className="pv-avatar-stage">
              <div className="pv-avatar-halo" />
              <div className="pv-avatar-nimbus" />
              <div className="pv-avatar-hex-ring">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="pv-hex-dot" style={{ transform: `rotate(${i * 60}deg) translateY(-58px)` }} />
                ))}
              </div>
              <div className="pv-avatar-glyph">{initial}</div>
              <div className={`pv-status-jewel pv-sj-${profile.status}`} />
            </div>

            {/* Identity */}
            <div className="pv-identity">
              <div className="pv-identity-rule-top">
                <span /><span className="pv-ir-glyph">✦</span><span />
              </div>
              <h1 className="pv-devotee-name">{profile.name}</h1>
              <p className="pv-devotee-epithet">{t("devotee")}</p>
              <div className="pv-status-ribbon">
                <span className={`pv-ribbon-gem pv-rg-${profile.status}`} />
                <span className="pv-ribbon-text">{profile.status}</span>
              </div>
              <div className="pv-identity-rule-bot">
                <span /><span className="pv-ir-glyph">◆</span><span />
              </div>
            </div>

            {/* Temple seal */}
            <div className="pv-temple-seal">
              <div className="pv-seal-ring" />
              <span className="pv-seal-om">ॐ</span>
            </div>
          </div>

          {/* ══ DIVIDER ══ */}
          <div className="pv-pillar">
            <div className="pv-pillar-cap top" />
            <div className="pv-pillar-shaft">
              {[...Array(7)].map((_, i) => (
                <div key={i} className="pv-pillar-rune" style={{ top: `${8 + i * 12}%` }}>◈</div>
              ))}
            </div>
            <div className="pv-pillar-cap bot" />
          </div>

          {/* ══ RIGHT SCROLL ══ */}
          <div className="pv-scroll">
            {/* Scroll header */}
            <div className="pv-scroll-header">
              <div className="pv-scroll-eyebrow">
                <span className="pv-ey-line" />
                <span className="pv-ey-text">✦ {t("lblFullName")} ✦</span>
                <span className="pv-ey-line" />
              </div>
              <h2 className="pv-scroll-name">{profile.name}</h2>
              <div className="pv-scroll-header-rule" />
            </div>

            {/* Info cards */}
            <div className="pv-cards-grid">
              {fields.map(({ icon, label, value, mono }, idx) => (
                <div className="pv-card" key={label} style={{ animationDelay: `${0.1 + idx * 0.08}s` }}>
                  <div className="pv-card-accent" />
                  <div className="pv-card-icon-col">
                    <div className="pv-card-icon">{icon}</div>
                  </div>
                  <div className="pv-card-content">
                    <span className="pv-card-label">{label}</span>
                    <span className={`pv-card-value${mono ? " mono" : ""}`}>
                      {value || <span className="info-empty">—</span>}
                    </span>
                  </div>
                  <div className="pv-card-corner tl" />
                  <div className="pv-card-corner br" />
                </div>
              ))}
            </div>

            {/* ID Proof */}
            {profile.id_proof_path && (
              <button className="pv-id-banner" onClick={() => setShowIdModal(true)}>
                <div className="pv-id-banner-icon"><FaIdCard /></div>
                <span className="pv-id-banner-label">{t("lblIdProof")}</span>
                <span className="pv-id-banner-action">{t("viewIdProof")} ◈</span>
              </button>
            )}

            {/* Edit CTA */}
            <button className="pv-edit-cta" onClick={onEdit}>
              <div className="pv-cta-shimmer" />
              <span className="pv-cta-icon"><FaEdit /></span>
              <span className="pv-cta-text">{t("editProfile")}</span>
              <span className="pv-cta-arrow">→</span>
            </button>
          </div>
        </div>

        {/* ── BOTTOM FOOTER BAR ── */}
        <div className="pv-footer-bar">
          <div className="pv-footer-inner">
            <span className="pv-footer-text">◆ {t("ftTempleName")} ◆</span>
          </div>
        </div>
      </div>

      {/* ID Proof Modal */}
{showIdModal && (
  <IdProofModal
    src={
      profile.id_proof_path?.startsWith("http")
        ? profile.id_proof_path
        : `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/${profile.id_proof_path}`
    }
    onClose={() => setShowIdModal(false)}
  />
)}
    </div>
  );
}

/* ─────────────────────────────────────────────────────
   PROFILE EDIT FORM — Matches register.js design
───────────────────────────────────────────────────── */
function ProfileEditForm({ profile, onCancel, onSaved }) {
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    name:    profile.name    || "",
    email:   profile.email   || "",
    place:   profile.place   || "",
    address: profile.address || "",
  });
  const [errors, setErrors]     = useState({});
  const [file, setFile]         = useState(null);
  const [loading, setLoading]   = useState(false);
  const [emailTaken, setEmailTaken] = useState(false);

  /* Live validation */
  useEffect(() => {
    const errs = {};
    if (formData.name && !formData.name.trim())    errs.name    = t("errName");
    if (formData.place && !formData.place.trim())  errs.place   = t("errPlace");
    if (formData.address && !formData.address.trim()) errs.address = t("errAddress");
    if (formData.email && formData.email.includes("@") && emailTaken) errs.email = t("errEmailTaken");
    setErrors(errs);
  }, [formData, emailTaken, t]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    /* Check email availability only if changed from original */
    if (name === "email" && value !== profile.email && value.includes("@")) {
      checkEmail(value);
    } else if (name === "email") {
      setEmailTaken(false);
    }
  };

  const checkEmail = async (email) => {
    try {
      const res  = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/check/email/${email}`);
      const data = await res.json();
      setEmailTaken(!data.available);
    } catch { /* ignore */ }
  };

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (!selected) return;
    if (selected.size > 2 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, idProof: t("errFileSize") }));
      return;
    }
    setFile(selected);
    setErrors(prev => ({ ...prev, idProof: null }));
  };

  const validate = () => {
    const errs = {};
    if (!formData.name.trim())    errs.name    = t("errName");
    if (!formData.place.trim())   errs.place   = t("errPlace");
    if (!formData.address.trim()) errs.address = t("errAddress");
    if (emailTaken)               errs.email   = t("errEmailTaken");
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);

    const token = localStorage.getItem("authToken");
    const payload = new FormData();
    Object.keys(formData).forEach(k => payload.append(k, formData[k]));
    if (file) payload.append("idProof", file);

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/profile`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
        body: payload,
      });
      const data = await res.json();
      if (res.ok) {
        /* Update localStorage user name if changed */
        const stored = localStorage.getItem("user");
        if (stored) {
          const u = JSON.parse(stored);
          u.name = formData.name;
          localStorage.setItem("user", JSON.stringify(u));
          window.dispatchEvent(new Event("authChange"));
        }
        onSaved({ ...profile, ...formData });
      } else {
        setErrors(prev => ({ ...prev, _server: data.error || t("errServer") }));
      }
    } catch {
      setErrors(prev => ({ ...prev, _server: t("errServer") }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="form-card">
      <div className="form-header">
        <div className="fh-icon">
          <div className="fh-ring" />
          <span className="fh-om">ॐ</span>
        </div>
        <h2 className="form-title">{t("editProfile")}</h2>
        <p className="form-subtitle">{t("editProfileSub")}</p>
        <div className="form-title-rule" />
      </div>

      <div className="form-body">
        {errors._server && (
          <div className="server-error-banner">{errors._server}</div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-grid">

            {/* Name */}
            <div className="input-group full-width">
              <label className="input-label">{t("lblFullName")} <span className="req-mark">*</span></label>
              <div className={`input-wrap ${errors.name ? "error" : ""}`}>
                <input type="text" name="name" className="form-input" placeholder={t("phFullName")} value={formData.name} onChange={handleChange} />
              </div>
              {errors.name && <span className="field-error">{errors.name}</span>}
            </div>

            {/* Mobile — read only */}
            <div className="input-group">
              <label className="input-label">{t("lblMobile")}</label>
              <div className="input-wrap input-wrap-disabled">
                <span className="input-prefix">🇮🇳 +91</span>
                <div className="input-sep" />
                <input type="tel" className="form-input" value={profile.mobile_number} disabled />
              </div>
              <span className="field-hint">{t("mobileCannotChange")}</span>
            </div>

            {/* Email */}
            <div className="input-group">
              <label className="input-label">{t("lblEmail")}</label>
              <div className={`input-wrap ${errors.email ? "error" : ""}`}>
                <input type="email" name="email" className="form-input" placeholder={t("phEmail")} value={formData.email} onChange={handleChange} />
              </div>
              {errors.email && <span className="field-error">{errors.email}</span>}
            </div>

            {/* Place */}
            <div className="input-group">
              <label className="input-label">{t("lblPlace")} <span className="req-mark">*</span></label>
              <div className={`input-wrap ${errors.place ? "error" : ""}`}>
                <input type="text" name="place" className="form-input" placeholder={t("phPlace")} value={formData.place} onChange={handleChange} />
              </div>
              {errors.place && <span className="field-error">{errors.place}</span>}
            </div>

            {/* Address */}
            <div className="input-group full-width">
              <label className="input-label">{t("lblAddress")} <span className="req-mark">*</span></label>
              <div className={`input-wrap ${errors.address ? "error" : ""}`}>
                <input type="text" name="address" className="form-input" placeholder={t("phAddress")} value={formData.address} onChange={handleChange} />
              </div>
              {errors.address && <span className="field-error">{errors.address}</span>}
            </div>

            {/* ID Proof — optional re-upload */}
            <div className="input-group full-width">
              <label className="input-label">{t("lblIdProof")} <span className="field-hint-inline">({t("optional")})</span></label>
              <div className={`upload-area ${errors.idProof ? "error" : ""}`} onClick={() => document.getElementById("profile-id-upload").click()}>
                <input id="profile-id-upload" type="file" className="file-input" accept="image/*,.pdf" onChange={handleFileChange} />
                <div className="upload-icon"><FaUpload /></div>
                <div className="upload-text">{file ? file.name : t("uploadText")}</div>
                <div className="upload-sub">{t("uploadSub")}</div>
              </div>
              {errors.idProof && <span className="field-error">{errors.idProof}</span>}
              {file && <div className="file-preview"><FaCheck /> {file.name} {t("fileSelected")}</div>}
            </div>

          </div>

          {/* Buttons */}
          <div className="edit-btn-row">
            <button type="button" className="cancel-btn" onClick={onCancel} disabled={loading}>
              {t("cancel")}
            </button>
            <button type="submit" className="submit-btn edit-submit" disabled={loading}>
              {loading ? t("saving") : t("saveChanges")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────
   MAIN — MyProfile
───────────────────────────────────────────────────── */
export default function MyProfile() {
  const navigate  = useNavigate();
  const { t }     = useTranslation();
  const [authChecked, setAuthChecked] = useState(false);

  const [profile,     setProfile]     = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [editing,     setEditing]     = useState(false);
  const [illusSymbol, setIllusSymbol] = useState("om");
  const [toast,       setToast]       = useState(null);

  /* Rotate medallion */
  useEffect(() => {
    const id = setInterval(() => setIllusSymbol(s => s === "om" ? "logo" : "om"), 3000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    const token = localStorage.getItem("authToken");

    if (storedUser && token) {
      try {
        const parsedUser = JSON.parse(storedUser);

        // If somehow an admin lands on /home, redirect them to /admin
        if (parsedUser.role === 'admin') {
          window.location.href = '/admin';
          return;
        }

        setAuthChecked(true); // ✅ valid devotee — show the page

      } catch (e) {
        localStorage.clear();
        window.location.href = '/login';
      }
    } else {
      // No session — send to login
      window.location.href = '/login';
    }
  }, []);


  /* Fetch profile */
  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (!token) { navigate("/login"); return; }

    fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/profile`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => {
        if (r.status === 401) { navigate("/login"); return null; }
        return r.json();
      })
      .then(data => {
        if (data) setProfile(data.profile);
      })
      .catch(() => setToast({ message: t("errServer"), type: "error" }))
      .finally(() => setLoading(false));
  }, [navigate, t]);

  const handleSaved = (updated) => {
    setProfile(updated);
    setEditing(false);
    setToast({ message: t("profileUpdated"), type: "success" });
  };

  if (!authChecked) return null;

  return (
    <>
      <PageStyles />
      <Navbar />

      {loading ? (
        <div className="profile-loading">
          <div className="loading-ring-wrap">
            <div className="loading-ring" />
            <div className="loading-ring-inner" />
            <div className="loading-om">ॐ</div>
          </div>
          <p className="loading-text">{t("loading") || "Loading..."}</p>
        </div>
      ) : profile ? (
        editing ? (
          <PageShell illusSymbol={illusSymbol}>
            <ProfileEditForm
              profile={profile}
              onCancel={() => setEditing(false)}
              onSaved={handleSaved}
            />
          </PageShell>
        ) : (
          <ProfileView profile={profile} onEdit={() => setEditing(true)} />
        )
      ) : (
        <div className="profile-loading">
          <p className="loading-text">{t("profileNotFound") || "Profile not found."}</p>
        </div>
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <Footer />
    </>
  );
}

/* ═══════════════════════════════════════════════════════
   STYLES
═══════════════════════════════════════════════════════ */
function PageStyles() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Cinzel+Decorative:wght@400;700;900&family=Cinzel:wght@400;500;600;700&family=EB+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500&display=swap');

      :root {
        --crimson-deep:  #1a0404;
        --crimson-mid:   #2c0808;
        --crimson-rich:  #3d0c0c;
        --gold-bright:   #e8c46a;
        --gold-mid:      #c9973a;
        --gold-dim:      #8a6020;
        --gold-pale:     #f5e4b0;
        --parchment:     #FDF3E3;
        --text-dark:     #1a0404;
        --text-mid:      #4a1a08;
      }

      *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }

      /* ═══ PAGE ═══ */
      .login-page { background: radial-gradient(ellipse at 30% 0%, rgba(201,151,58,0.07) 0%, transparent 60%), var(--parchment); min-height: calc(100vh - 160px); display: flex; align-items: center; justify-content: center; padding: 48px 24px; }

      /* ═══ OUTER CARD ═══ */
      .outer-card { width: 100%; max-width: 1100px; border: 1px solid rgba(201,151,58,0.3); box-shadow: 0 0 0 4px rgba(201,151,58,0.06), 0 0 0 8px rgba(201,151,58,0.03), 0 32px 80px rgba(26,4,4,0.22); clip-path: polygon(0 20px, 20px 0, calc(100% - 20px) 0, 100% 20px, 100% calc(100% - 20px), calc(100% - 20px) 100%, 20px 100%, 0 calc(100% - 20px)); overflow: hidden; animation: outerCardIn 0.6s cubic-bezier(0.23,1,0.32,1) both; }
      @keyframes outerCardIn { from { opacity: 0; transform: scale(0.97) translateY(20px); } to { opacity: 1; transform: scale(1) translateY(0); } }

      .outer-card-strip { height: 3px; background: linear-gradient(90deg, transparent, var(--gold-dim) 10%, var(--gold-bright) 30%, #fff8e7 50%, var(--gold-bright) 70%, var(--gold-dim) 90%, transparent); }
      .outer-card-strip.bottom { background: linear-gradient(90deg, transparent, var(--gold-dim) 15%, var(--gold-mid) 50%, var(--gold-dim) 85%, transparent); }

      /* ═══ SPLIT ═══ */
      .login-split { display: grid; grid-template-columns: 1fr 1.2fr; min-height: 650px; }
      .login-left { background: radial-gradient(ellipse at 50% 25%, rgba(120,25,25,0.55) 0%, transparent 60%), radial-gradient(ellipse at 20% 80%, rgba(60,10,10,0.4) 0%, transparent 50%), linear-gradient(160deg, #2c0a0a 0%, #1a0404 55%, #0e0101 100%); display: flex; align-items: center; justify-content: center; padding: 52px 36px; position: relative; overflow: hidden; border-right: 1px solid rgba(201,151,58,0.15); }
      .login-right { background: var(--parchment); display: flex; align-items: center; justify-content: center; padding: 52px 44px; position: relative; overflow: hidden; }

      /* ═══ ILLUSTRATOR ═══ */
      .illus-panel { position: relative; display: flex; flex-direction: column; align-items: center; z-index: 1; width: 100%; max-width: 340px; }
      .illus-rings { position: absolute; top: -10px; left: 50%; transform: translateX(-50%); width: 290px; height: 290px; pointer-events: none; }
      .illus-ring { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); border-radius: 50%; border: 1px solid rgba(232,196,106,0.08); animation: gentlePulse 4s ease-in-out infinite alternate; }
      .illus-ring:nth-child(odd) { border-style: dashed; }
      @keyframes gentlePulse { from { opacity: 0.4; } to { opacity: 0.9; } }
      .illus-medallion { width: 110px; height: 110px; position: relative; display: flex; align-items: center; justify-content: center; margin-bottom: 28px; margin-top: 40px; flex-shrink: 0; }
      .medallion-outer-ring { position: absolute; inset: 0; border-radius: 50%; background: conic-gradient(from 0deg, transparent 0deg, var(--gold-bright) 40deg, var(--gold-mid) 80deg, transparent 120deg, var(--gold-bright) 160deg, transparent 200deg, var(--gold-mid) 240deg, transparent 280deg, var(--gold-bright) 320deg, transparent 360deg) border-box; border: 2px solid transparent; -webkit-mask: linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0); -webkit-mask-composite: destination-out; mask-composite: exclude; animation: slowSpin 10s linear infinite; }
      @keyframes slowSpin { to { transform: rotate(360deg); } }
      .medallion-inner { position: absolute; inset: 10px; border-radius: 50%; background: radial-gradient(circle, #3d1010 0%, #1a0404 70%, #0e0101 100%); border: 1px solid rgba(232,196,106,0.25); box-shadow: inset 0 0 20px rgba(0,0,0,0.7), 0 0 20px rgba(232,196,106,0.08); overflow: hidden; display: flex; align-items: center; justify-content: center; }
      .medallion-face { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; opacity: 0; transform: scale(0.7) rotateY(90deg); transition: opacity 0.6s ease, transform 0.6s ease; border-radius: 50%; }
      .medallion-face.visible { opacity: 1; transform: scale(1) rotateY(0deg); }
      .illus-om { font-family: 'Cinzel Decorative', cursive; font-size: 36px; background: linear-gradient(180deg, #fff8e7 0%, var(--gold-bright) 50%, var(--gold-mid) 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; filter: drop-shadow(0 2px 8px rgba(232,196,106,0.6)); }
      .illus-logo-img { width: 62px; height: 62px; object-fit: contain; filter: drop-shadow(0 0 8px rgba(232,196,106,0.4)); }
      .medallion-halo { position: absolute; inset: -8px; border-radius: 50%; background: radial-gradient(circle, rgba(232,196,106,0.12) 0%, transparent 70%); pointer-events: none; }
      .illus-info { display: flex; flex-direction: column; align-items: center; gap: 5px; margin-bottom: 20px; width: 100%; }
      .illus-rule { width: 70%; height: 1px; background: linear-gradient(90deg, transparent, rgba(232,196,106,0.35), transparent); margin: 6px 0; }
      .illus-name { font-family: 'Cinzel Decorative', cursive; font-size: 15px; font-weight: 900; letter-spacing: 1px; background: linear-gradient(135deg, #fffaf0, var(--gold-bright), var(--gold-mid)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; text-align: center; line-height: 1.4; }
      .illus-sub { font-family: 'Cinzel', serif; font-size: 10px; font-weight: 600; letter-spacing: 1.5px; text-transform: uppercase; color: rgba(232,196,106,0.5); text-align: center; }
      .illus-loc { font-family: 'Cinzel', serif; font-size: 8.5px; letter-spacing: 2px; text-transform: uppercase; color: rgba(232,196,106,0.3); text-align: center; }
      .illus-quote { margin-bottom: 20px; padding: 0 8px; }
      .illus-quote p { font-family: 'EB Garamond', serif; font-style: italic; font-size: 13px; color: rgba(245,228,176,0.4); text-align: center; line-height: 1.75; }
      .illus-dots { display: flex; gap: 8px; align-items: center; }
      .illus-dot { width: 5px; height: 5px; background: var(--gold-bright); transform: rotate(45deg); display: block; }

      /* ═══ FORM CARD ═══ */
      .form-card { width: 100%; max-width: 650px; position: relative; z-index: 1; }
      .form-header { text-align: center; padding: 20px 32px 10px; }
      .fh-icon { width: 54px; height: 54px; position: relative; display: flex; align-items: center; justify-content: center; margin: 0 auto 14px; }
      .fh-ring { position: absolute; inset: 0; border-radius: 50%; background: conic-gradient(from 0deg, transparent 0deg, var(--gold-bright) 60deg, transparent 120deg, var(--gold-mid) 180deg, transparent 240deg, var(--gold-bright) 300deg, transparent 360deg) border-box; border: 1.5px solid transparent; -webkit-mask: linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0); -webkit-mask-composite: destination-out; mask-composite: exclude; animation: slowSpin 8s linear infinite; }
      .fh-om { font-family: 'Cinzel Decorative', cursive; font-size: 18px; background: linear-gradient(180deg, #fff8e7, var(--gold-bright), var(--gold-mid)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; position: relative; z-index: 1; }
      .form-title { font-family: 'Cinzel Decorative', cursive; font-size: 18px; font-weight: 900; letter-spacing: 0.5px; background: linear-gradient(135deg, #3d0c0c, var(--crimson-deep)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; margin-bottom: 6px; }
      .form-subtitle { font-family: 'EB Garamond', serif; font-style: italic; font-size: 14px; color: rgba(74,26,8,0.55); line-height: 1.5; }
      .form-title-rule { width: 60%; height: 1px; background: linear-gradient(90deg, transparent, rgba(201,151,58,0.35), transparent); margin: 16px auto 0; }
      .form-body { padding: 24px 32px 36px; display: flex; flex-direction: column; gap: 16px; }
      .form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
      .full-width { grid-column: span 2; }
      .input-group { display: flex; flex-direction: column; gap: 6px; position: relative; }
      .input-label { font-family: 'Cinzel', serif; font-size: 9px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: var(--gold-dim); }
      .req-mark { color: #b52020; font-weight: 900; margin-left: 2px; }
      .input-wrap { display: flex; align-items: center; background: #fff9f0; border: 1px solid rgba(201,151,58,0.28); transition: border-color 0.3s, box-shadow 0.3s, background 0.3s; clip-path: polygon(10px 0%, calc(100% - 10px) 0%, 100% 50%, calc(100% - 10px) 100%, 10px 100%, 0% 50%); overflow: hidden; position: relative; }
      .input-wrap:focus-within { border-color: var(--gold-mid); background: #fffaf0; box-shadow: 0 0 0 3px rgba(201,151,58,0.12); }
      .input-wrap.error { border-color: #b52020; background: rgba(181,32,32,0.02); }
      .input-wrap-disabled { background: rgba(201,151,58,0.06) !important; cursor: not-allowed; opacity: 0.7; }
      .input-wrap-disabled input { cursor: not-allowed; color: rgba(74,26,8,0.5); }
      .input-prefix { font-family: 'Cinzel', serif; font-size: 11px; font-weight: 600; color: var(--text-mid); padding: 13px 12px 13px 20px; border-right: 1px solid rgba(201,151,58,0.2); flex-shrink: 0; }
      .input-sep { width: 1px; height: 20px; background: rgba(201,151,58,0.2); flex-shrink: 0; }
      .form-input { flex: 1; background: transparent; border: none; outline: none; font-family: 'EB Garamond', serif; font-size: 15px; color: var(--text-dark); padding: 13px 14px; }
      .form-input::placeholder { color: rgba(74,26,8,0.25); font-style: italic; }
      .field-error { font-size: 10px; color: #b52020; margin-top: 4px; font-family: 'EB Garamond', serif; font-style: italic; }
      .field-hint { font-size: 9.5px; color: rgba(74,26,8,0.4); margin-top: 3px; font-family: 'EB Garamond', serif; font-style: italic; }
      .field-hint-inline { font-family: 'EB Garamond', serif; font-style: italic; font-size: 10px; color: rgba(74,26,8,0.4); letter-spacing: 0; text-transform: none; }

      /* Upload */
      .upload-area { border: 1px dashed rgba(201,151,58,0.4); background: rgba(255,249,240,0.5); padding: 20px; text-align: center; cursor: pointer; transition: all 0.3s; position: relative; clip-path: polygon(10px 0%, 100% 0%, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0% 100%, 0% 10px); }
      .upload-area:hover { border-color: var(--gold-mid); background: rgba(255,250,240,0.8); }
      .upload-icon { font-size: 24px; color: var(--gold-dim); margin-bottom: 8px; }
      .upload-text { font-size: 13px; color: var(--text-mid); font-family: 'EB Garamond', serif; }
      .upload-sub { font-size: 11px; color: rgba(74,26,8,0.5); margin-top: 4px; }
      .file-input { position: absolute; inset: 0; opacity: 0; cursor: pointer; }
      .file-preview { margin-top: 8px; font-size: 11px; color: #2a6b2a; display: flex; align-items: center; justify-content: center; gap: 6px; font-family: 'Cinzel', serif; }

      /* Buttons */
      .submit-btn { font-family: 'Cinzel', serif; font-size: 11px; font-weight: 700; letter-spacing: 2.5px; text-transform: uppercase; color: var(--crimson-deep); background: linear-gradient(135deg, #7a4e0c 0%, var(--gold-mid) 20%, var(--gold-bright) 40%, #fff8e7 55%, var(--gold-bright) 70%, var(--gold-mid) 85%, #6b4209 100%); border: none; padding: 15px 28px; cursor: pointer; width: 100%; display: flex; align-items: center; justify-content: center; gap: 10px; clip-path: polygon(14px 0%, calc(100% - 14px) 0%, 100% 50%, calc(100% - 14px) 100%, 14px 100%, 0% 50%); box-shadow: 0 6px 24px rgba(201,151,58,0.3), inset 0 1px 0 rgba(255,255,255,0.25); transition: all 0.35s ease; position: relative; overflow: hidden; }
      .submit-btn::before { content: ''; position: absolute; top: -50%; left: -70%; width: 40%; height: 200%; background: rgba(255,255,255,0.25); transform: skewX(-18deg); transition: left 0.55s ease; pointer-events: none; }
      .submit-btn:hover::before { left: 130%; }
      .submit-btn:hover { transform: translateY(-2px); box-shadow: 0 12px 32px rgba(201,151,58,0.5); filter: brightness(1.05); }
      .submit-btn:disabled { opacity: 0.38; cursor: not-allowed; transform: none; box-shadow: none; }
      .edit-submit { width: auto; flex: 1; }

      /* Cancel button */
      .cancel-btn { font-family: 'Cinzel', serif; font-size: 11px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: rgba(74,26,8,0.6); background: transparent; border: 1px solid rgba(201,151,58,0.3); padding: 15px 28px; cursor: pointer; clip-path: polygon(14px 0%, calc(100% - 14px) 0%, 100% 50%, calc(100% - 14px) 100%, 14px 100%, 0% 50%); transition: all 0.3s; }
      .cancel-btn:hover { border-color: rgba(201,151,58,0.6); color: var(--text-dark); background: rgba(201,151,58,0.06); }
      .cancel-btn:disabled { opacity: 0.38; cursor: not-allowed; }

      .edit-btn-row { display: flex; gap: 14px; margin-top: 28px; }

      /* Server error banner */
      .server-error-banner { background: rgba(181,32,32,0.08); border: 1px solid rgba(181,32,32,0.25); padding: 12px 16px; font-family: 'EB Garamond', serif; font-style: italic; font-size: 13px; color: #8b1a1a; clip-path: polygon(8px 0%, calc(100% - 8px) 0%, 100% 50%, calc(100% - 8px) 100%, 8px 100%, 0% 50%); text-align: center; }

      /* ═══ PROFILE VIEW ═══ */
      .profile-avatar-large { width: 80px; height: 80px; position: relative; display: flex; align-items: center; justify-content: center; margin: 0 auto 16px; }
      .profile-avatar-ring { position: absolute; inset: 0; border-radius: 50%; background: conic-gradient(from 0deg, transparent 0deg, var(--gold-bright) 60deg, transparent 120deg, var(--gold-mid) 180deg, transparent 240deg, var(--gold-bright) 300deg, transparent 360deg) border-box; border: 2px solid transparent; -webkit-mask: linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0); -webkit-mask-composite: destination-out; mask-composite: exclude; animation: slowSpin 8s linear infinite; }
      .profile-avatar-letter { width: 66px; height: 66px; border-radius: 50%; background: linear-gradient(135deg, #7a4e0c 0%, var(--gold-bright) 45%, #f7edd0 65%, var(--gold-mid) 100%); display: flex; align-items: center; justify-content: center; color: #1a0404; font-family: 'Cinzel Decorative', cursive; font-size: 28px; font-weight: 900; position: relative; z-index: 1; box-shadow: 0 0 20px rgba(232,196,106,0.4); }

      .profile-status-badge { display: inline-flex; align-items: center; gap: 7px; margin-top: 10px; padding: 5px 16px; border: 1px solid rgba(201,151,58,0.25); background: rgba(201,151,58,0.06); clip-path: polygon(8px 0%, calc(100% - 8px) 0%, 100% 50%, calc(100% - 8px) 100%, 8px 100%, 0% 50%); }
      .status-dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; }
      .status-dot.status-approved { background: #2a6b2a; box-shadow: 0 0 6px rgba(42,107,42,0.6); }
      .status-dot.status-pending  { background: #c9973a; box-shadow: 0 0 6px rgba(201,151,58,0.6); }
      .status-dot.status-rejected { background: #8b1a1a; box-shadow: 0 0 6px rgba(139,26,26,0.6); }
      .status-text { font-family: 'Cinzel', serif; font-size: 9px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: var(--gold-dim); }

      /* Profile info rows */
      .profile-info-grid { display: flex; flex-direction: column; gap: 0; border: 1px solid rgba(201,151,58,0.18); clip-path: polygon(10px 0%, calc(100% - 10px) 0%, 100% 10px, 100% calc(100% - 10px), calc(100% - 10px) 100%, 10px 100%, 0% calc(100% - 10px), 0% 10px); overflow: hidden; }
      .info-row { display: flex; align-items: flex-start; gap: 16px; padding: 14px 20px; background: rgba(255,249,240,0.6); border-bottom: 1px solid rgba(201,151,58,0.1); transition: background 0.2s; }
      .info-row:last-child { border-bottom: none; }
      .info-row:hover { background: rgba(255,250,240,0.95); }
      .info-icon { color: var(--gold-mid); font-size: 14px; margin-top: 3px; flex-shrink: 0; width: 18px; text-align: center; }
      .info-content { display: flex; flex-direction: column; gap: 3px; min-width: 0; flex: 1; }
      .info-label { font-family: 'Cinzel', serif; font-size: 8.5px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: var(--gold-dim); }
      .info-value { font-family: 'EB Garamond', serif; font-size: 15px; color: var(--text-dark); line-height: 1.4; word-break: break-word; }
      .info-value.mono { font-family: 'Cinzel', serif; font-size: 13px; letter-spacing: 1px; }
      .info-empty { color: rgba(74,26,8,0.2); font-style: italic; }

      /* ID proof link */
      .id-proof-row { display: flex; align-items: center; justify-content: space-between; padding: 10px 16px; border: 1px solid rgba(201,151,58,0.2); background: rgba(255,249,240,0.5); }
      .id-proof-label { font-family: 'Cinzel', serif; font-size: 9px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: var(--gold-dim); }
      .id-proof-link { font-family: 'Cinzel', serif; font-size: 9.5px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; color: var(--gold-mid); text-decoration: none; display: flex; align-items: center; gap: 6px; transition: color 0.2s; }
      .id-proof-link:hover { color: var(--crimson-deep); }

      /* Loading */
      .profile-loading { min-height: 50vh; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 24px; padding: 120px 0; background: var(--parchment); }
      .loading-ring-wrap { position: relative; width: 80px; height: 80px; display: flex; align-items: center; justify-content: center; background: rgba(201,151,58,0.05); border-radius: 50%; box-shadow: inset 0 0 20px rgba(201,151,58,0.15); }
      .loading-ring { position: absolute; inset: -6px; border-radius: 50%; border: 1.5px dashed rgba(201,151,58,0.4); animation: prfSpin 6s linear infinite; }
      .loading-ring-inner { position: absolute; inset: 4px; border-radius: 50%; border: 1px dotted rgba(201,151,58,0.6); animation: prfSpin 10s linear infinite reverse; }
      @keyframes prfSpin { to { transform: rotate(360deg); } }
      .loading-om { position: relative; z-index: 1; font-family: 'Cinzel Decorative', cursive; font-size: 38px; background: linear-gradient(135deg, #fffaf0 0%, var(--gold-bright) 40%, var(--gold-mid) 70%, #a06a15 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; animation: prfPulse 1.5s ease-in-out infinite alternate; filter: drop-shadow(0 2px 8px rgba(201,151,58,0.4)); }
      .loading-text { font-family: 'EB Garamond', serif; font-style: italic; font-size: 16px; color: rgba(74,26,8,0.5); letter-spacing: 1px; animation: prfPulse 1.5s ease-in-out infinite alternate 0.5s; }
      @keyframes prfPulse { from { opacity: 0.3; } to { opacity: 1; } }

      /* ══════════════════════════════════════════
         TOAST
      ══════════════════════════════════════════ */
      .toast-container { position: fixed; top: 28px; right: 28px; z-index: 2000; animation: toastSlideIn 0.45s cubic-bezier(0.23, 1, 0.32, 1) both; }
      @keyframes toastSlideIn { from { opacity: 0; transform: translateX(60px) scale(0.95); } to { opacity: 1; transform: translateX(0) scale(1); } }
      .toast-card { position: relative; width: 340px; background: linear-gradient(145deg, #FDF3E3 0%, #faf0dc 55%, #f5e8cc 100%); border: 1px solid var(--border-color); padding: 0 0 14px 0; overflow: hidden; clip-path: polygon(0 12px, 12px 0, calc(100% - 12px) 0, 100% 12px, 100% calc(100% - 12px), calc(100% - 12px) 100%, 12px 100%, 0 calc(100% - 12px)); box-shadow: 0 4px 6px rgba(26,4,4,0.06), 0 12px 32px rgba(26,4,4,0.14), 0 0 0 1px rgba(201,151,58,0.08), inset 0 1px 0 rgba(255,255,255,0.7); }
      .toast-top-bar { height: 3px; background: linear-gradient(90deg, transparent 0%, var(--accent) 20%, var(--accent) 80%, transparent 100%); margin-bottom: 16px; }
      .toast-corner { position: absolute; width: 10px; height: 10px; border-color: rgba(201,151,58,0.45); border-style: solid; }
      .toast-corner.tl { top: 12px; left: 12px; border-width: 1px 0 0 1px; }
      .toast-corner.tr { top: 12px; right: 12px; border-width: 1px 1px 0 0; }
      .toast-corner.bl { bottom: 8px; left: 12px; border-width: 0 0 1px 1px; }
      .toast-corner.br { bottom: 8px; right: 12px; border-width: 0 1px 1px 0; }
      .toast-medallion { position: absolute; left: 18px; top: 26px; width: 36px; height: 36px; border-radius: 50%; border: 1px solid; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
      .toast-medallion-ring { position: absolute; inset: -3px; border-radius: 50%; border: 1px dashed rgba(201,151,58,0.3); animation: toastSpin 8s linear infinite; }
      @keyframes toastSpin { to { transform: rotate(360deg); } }
      .toast-icon-inner { font-size: 14px; position: relative; z-index: 1; }
      .toast-content { padding: 0 40px 0 66px; }
      .toast-label-row { display: flex; align-items: center; gap: 6px; margin-bottom: 5px; }
      .toast-symbol { font-family: 'Cinzel Decorative', cursive; font-size: 9px; opacity: 0.7; }
      .toast-title { font-family: 'Cinzel', serif; font-size: 10px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; flex: 1; }
      .toast-rule { height: 1px; margin-bottom: 7px; }
      .toast-message { font-family: 'EB Garamond', serif; font-style: italic; font-size: 14px; color: rgba(74,26,8,0.75); line-height: 1.5; }
      .toast-bottom-rule { height: 1px; margin: 12px 20px 0; background: linear-gradient(90deg, transparent, rgba(201,151,58,0.25), transparent); }
      .toast-close { position: absolute; top: 22px; right: 14px; background: none; border: none; font-size: 13px; cursor: pointer; opacity: 0.45; line-height: 1; padding: 2px 4px; transition: opacity 0.2s; font-family: sans-serif; }
      .toast-close:hover { opacity: 0.9; }

      /* ═══ RESPONSIVE ═══ */
      @media (max-width: 860px) {
        .login-page { padding: 0; align-items: stretch; min-height: calc(100vh - 160px); background: var(--parchment); }
        .outer-card { clip-path: none; border: none; box-shadow: none; width: 100%; }
        .login-split { grid-template-columns: 1fr; min-height: auto; }
        .login-left { display: none; }
        .login-right { padding: 40px 24px; background: transparent; }
        .form-card { clip-path: none; border-radius: 2px; max-width: 100%; }
        .outer-card-strip { display: none; }
        .form-grid { grid-template-columns: 1fr; gap: 16px; }
        .full-width { grid-column: span 1; }
        .edit-btn-row { flex-direction: column; }
        .edit-submit, .cancel-btn { width: 100%; clip-path: polygon(14px 0%, calc(100% - 14px) 0%, 100% 50%, calc(100% - 14px) 100%, 14px 100%, 0% 50%); }
        .toast-container { top: 16px; right: 16px; }
        .toast-card { width: 300px; }
      }
      @media (max-width: 480px) {
        .login-right { padding: 28px 16px; }
        .form-body { padding: 20px 18px 28px; gap: 14px; }
        .form-header { padding: 24px 18px 0; }
        .form-title { font-size: 15px; }
        .toast-card { width: 280px; }
      }

      /* ══════════════════════════════════════════════════
         PROFILE VIEW — Grand Temple Manuscript Design
      ══════════════════════════════════════════════════ */

      /* Page wrapper */
      .pv-page {
        min-height: calc(100vh - 160px);
        background:
          radial-gradient(ellipse 60% 40% at 15% 10%, rgba(201,151,58,0.10) 0%, transparent 60%),
          radial-gradient(ellipse 50% 35% at 85% 90%, rgba(120,20,20,0.08) 0%, transparent 55%),
          radial-gradient(ellipse 40% 50% at 50% 50%, rgba(253,243,227,0.3) 0%, transparent 70%),
          linear-gradient(160deg, #fef8ed 0%, #fdf3e3 40%, #f8e8d0 100%);
        display: flex; align-items: center; justify-content: center;
        padding: 40px 20px; position: relative; overflow: hidden;
      }

      /* Floating ambient particles */
      .pv-ambient { position: absolute; inset: 0; pointer-events: none; overflow: hidden; }
      .pv-particle {
        position: absolute; bottom: -10px;
        width: 3px; height: 3px; border-radius: 50%;
        background: var(--gold-bright); opacity: 0;
        animation: particleFloat linear infinite;
      }
      @keyframes particleFloat {
        0%   { opacity: 0; transform: translateY(0) scale(0.5); }
        10%  { opacity: 0.35; }
        90%  { opacity: 0.15; }
        100% { opacity: 0; transform: translateY(-100vh) scale(1.5); }
      }

      /* ── MANUSCRIPT CARD ── */
      .pv-manuscript {
        width: 100%; max-width: 980px;
        border: 1px solid rgba(201,151,58,0.35);
        border-top: none; border-bottom: none;
        box-shadow:
          0 0 0 1px rgba(201,151,58,0.10),
          0 0 0 5px rgba(201,151,58,0.04),
          0 40px 100px rgba(26,4,4,0.20),
          inset 0 0 60px rgba(201,151,58,0.02);
        overflow: hidden;
        animation: manuscriptIn 0.7s cubic-bezier(0.23,1,0.32,1) both;
        position: relative;
      }
      @keyframes manuscriptIn {
        from { opacity: 0; transform: translateY(28px) scale(0.975); }
        to   { opacity: 1; transform: translateY(0) scale(1); }
      }

      /* ── CROWN BAR (top) ── */
      .pv-crown-bar {
        background: linear-gradient(135deg, #1a0404 0%, #2c0a0a 40%, #3d1010 60%, #2c0a0a 80%, #1a0404 100%);
        border-bottom: 1px solid rgba(201,151,58,0.25);
        padding: 0;
        position: relative;
        overflow: hidden;
      }
      .pv-crown-bar::before {
        content: '';
        position: absolute; inset: 0;
        background: repeating-linear-gradient(90deg, transparent 0px, transparent 18px, rgba(201,151,58,0.04) 18px, rgba(201,151,58,0.04) 19px);
      }
      .pv-crown-inner {
        display: flex; align-items: center; justify-content: center;
        padding: 12px 32px; gap: 0; position: relative; z-index: 1;
      }
      .pv-crown-wing {
        flex: 1; height: 1px;
        background: linear-gradient(90deg, transparent, rgba(232,196,106,0.5) 40%, rgba(232,196,106,0.8) 100%);
      }
      .pv-crown-wing.right { background: linear-gradient(270deg, transparent, rgba(232,196,106,0.5) 40%, rgba(232,196,106,0.8) 100%); }
      .pv-crown-center {
        display: flex; flex-direction: column; align-items: center;
        padding: 0 28px; gap: 4px;
      }
      .pv-crown-om {
        font-family: 'Cinzel Decorative', cursive; font-size: 22px;
        background: linear-gradient(180deg, #fff8e7 0%, var(--gold-bright) 50%, var(--gold-mid) 100%);
        -webkit-background-clip: text; -webkit-text-fill-color: transparent;
        filter: drop-shadow(0 2px 6px rgba(232,196,106,0.6));
        line-height: 1;
      }
      .pv-crown-diamonds { display: flex; gap: 5px; align-items: center; }
      .pv-cdiamond {
        font-size: 5px; color: var(--gold-mid); opacity: 0.6;
        animation: diamondPulse 2.5s ease-in-out infinite alternate;
      }
      .pv-cdiamond:nth-child(3) { font-size: 8px; opacity: 0.9; color: var(--gold-bright); }
      @keyframes diamondPulse { from { opacity: 0.3; } to { opacity: 0.8; } }

      /* ── MAIN BODY ── */
      .pv-body {
        display: grid;
        grid-template-columns: 300px 40px 1fr;
        min-height: 520px;
      }

      /* ══ SANCTUM (left dark panel) ══ */
      .pv-sanctum {
        background:
          radial-gradient(ellipse at 50% 25%, rgba(120,25,25,0.6) 0%, transparent 55%),
          radial-gradient(ellipse at 15% 75%, rgba(60,10,10,0.5) 0%, transparent 50%),
          linear-gradient(175deg, #2c0a0a 0%, #1a0404 50%, #0d0101 100%);
        display: flex; flex-direction: column; align-items: center; justify-content: center;
        padding: 52px 28px 44px; position: relative; overflow: hidden; gap: 0;
      }
      .pv-sanctum::after {
        content: '';
        position: absolute; right: 0; top: 0; bottom: 0; width: 1px;
        background: linear-gradient(180deg, transparent, rgba(201,151,58,0.4) 20%, rgba(232,196,106,0.6) 50%, rgba(201,151,58,0.4) 80%, transparent);
      }

      /* Mandala background */
      .pv-mandala {
        position: absolute; top: 50%; left: 50%;
        transform: translate(-50%, -50%);
        pointer-events: none;
      }
      .pv-mandala-ring {
        position: absolute; top: 50%; left: 50%;
        transform: translate(-50%, -50%);
        border-radius: 50%; border: 1px solid rgba(232,196,106,0.06);
      }
      .pv-mr-0 { border-style: solid;  animation: gentlePulse 5s ease-in-out infinite alternate; }
      .pv-mr-1 { border-style: dashed; animation: gentlePulse 5s ease-in-out 0.5s infinite alternate; }
      .pv-mr-2 { border-style: solid;  animation: gentlePulse 5s ease-in-out 1.0s infinite alternate; border-color: rgba(232,196,106,0.04); }
      .pv-mr-3 { border-style: dashed; animation: gentlePulse 5s ease-in-out 1.5s infinite alternate; }
      .pv-mr-4 { border-style: solid;  animation: gentlePulse 5s ease-in-out 2.0s infinite alternate; border-color: rgba(232,196,106,0.03); }
      .pv-mandala-core {
        position: absolute; top: 50%; left: 50%;
        transform: translate(-50%, -50%);
        width: 20px; height: 20px; border-radius: 50%;
        background: radial-gradient(circle, rgba(232,196,106,0.15), transparent 70%);
      }

      /* Avatar stage */
      .pv-avatar-stage {
        position: relative; width: 116px; height: 116px;
        display: flex; align-items: center; justify-content: center;
        margin-bottom: 24px; flex-shrink: 0;
        z-index: 2;
      }
      .pv-avatar-halo {
        position: absolute; inset: -14px; border-radius: 50%;
        background: radial-gradient(circle, rgba(232,196,106,0.18) 0%, transparent 65%);
        animation: haloBreath 3s ease-in-out infinite alternate;
      }
      @keyframes haloBreath { from { opacity: 0.5; transform: scale(0.95); } to { opacity: 1; transform: scale(1.05); } }
      .pv-avatar-nimbus {
        position: absolute; inset: -4px; border-radius: 50%;
        background: conic-gradient(from 0deg,
          transparent 0deg, var(--gold-bright) 45deg, transparent 90deg,
          var(--gold-mid) 135deg, transparent 180deg,
          var(--gold-bright) 225deg, transparent 270deg,
          var(--gold-mid) 315deg, transparent 360deg
        ) border-box;
        border: 1.5px solid transparent;
        -webkit-mask: linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0);
        -webkit-mask-composite: destination-out; mask-composite: exclude;
        animation: slowSpin 10s linear infinite;
      }
      .pv-avatar-hex-ring {
        position: absolute; inset: 0;
        animation: slowSpin 20s linear infinite reverse;
      }
      .pv-hex-dot {
        position: absolute; top: 50%; left: 50%;
        width: 5px; height: 5px; margin: -2.5px 0 0 -2.5px;
        border-radius: 50%; background: var(--gold-bright); opacity: 0.5;
        box-shadow: 0 0 4px rgba(232,196,106,0.6);
      }
      .pv-avatar-glyph {
        width: 92px; height: 92px; border-radius: 50%;
        background: linear-gradient(140deg, #6b4209 0%, #c9973a 30%, var(--gold-bright) 50%, #f7edd0 65%, var(--gold-mid) 85%, #5a3607 100%);
        display: flex; align-items: center; justify-content: center;
        color: #1a0404; font-family: 'Cinzel Decorative', cursive;
        font-size: 38px; font-weight: 900; position: relative; z-index: 1;
        box-shadow: 0 0 32px rgba(232,196,106,0.5), 0 8px 24px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.25);
        letter-spacing: -1px;
      }
      .pv-status-jewel {
        position: absolute; bottom: 4px; right: 4px;
        width: 18px; height: 18px; border-radius: 50%;
        border: 2.5px solid #1a0404; z-index: 3;
        box-shadow: 0 0 0 1px rgba(232,196,106,0.3);
      }
      .pv-sj-approved { background: radial-gradient(circle at 35% 35%, #4caf50, #2a6b2a); box-shadow: 0 0 10px rgba(42,107,42,0.8); }
      .pv-sj-pending  { background: radial-gradient(circle at 35% 35%, #f0b84a, #c9973a); box-shadow: 0 0 10px rgba(201,151,58,0.8); }
      .pv-sj-rejected { background: radial-gradient(circle at 35% 35%, #c43030, #8b1a1a); box-shadow: 0 0 10px rgba(139,26,26,0.8); }

      /* Identity text block */
      .pv-identity {
        display: flex; flex-direction: column; align-items: center;
        gap: 6px; z-index: 2; width: 100%;
      }
      .pv-identity-rule-top,
      .pv-identity-rule-bot {
        display: flex; align-items: center; gap: 8px; width: 80%;
      }
      .pv-identity-rule-top span:not(.pv-ir-glyph),
      .pv-identity-rule-bot span:not(.pv-ir-glyph) {
        flex: 1; height: 1px;
        background: linear-gradient(90deg, transparent, rgba(232,196,106,0.35));
      }
      .pv-identity-rule-bot span:not(.pv-ir-glyph) {
        background: linear-gradient(90deg, rgba(232,196,106,0.35), transparent);
      }
      .pv-identity-rule-top span:last-child { background: linear-gradient(90deg, rgba(232,196,106,0.35), transparent); }
      .pv-ir-glyph { font-size: 7px; color: rgba(232,196,106,0.55); flex-shrink: 0; }

      .pv-devotee-name {
        font-family: 'Cinzel Decorative', cursive; font-size: 17px; font-weight: 900;
        background: linear-gradient(135deg, #fff8e7 10%, var(--gold-bright) 40%, #f7d98a 60%, var(--gold-mid) 100%);
        -webkit-background-clip: text; -webkit-text-fill-color: transparent;
        text-align: center; line-height: 1.4;
        letter-spacing: 0.3px; margin: 2px 0;
        filter: drop-shadow(0 1px 3px rgba(0,0,0,0.3));
      }
      .pv-devotee-epithet {
        font-family: 'EB Garamond', serif; font-style: italic; font-size: 12.5px;
        color: rgba(245,228,176,0.4); letter-spacing: 0.5px;
      }
      .pv-status-ribbon {
        display: inline-flex; align-items: center; gap: 8px;
        margin: 6px 0 4px;
        padding: 6px 18px; border: 1px solid rgba(201,151,58,0.22);
        background: rgba(201,151,58,0.06);
        clip-path: polygon(10px 0%, calc(100% - 10px) 0%, 100% 50%, calc(100% - 10px) 100%, 10px 100%, 0% 50%);
      }
      .pv-ribbon-gem { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; }
      .pv-rg-approved { background: #2a6b2a; box-shadow: 0 0 6px rgba(42,107,42,0.7); }
      .pv-rg-pending  { background: #c9973a; box-shadow: 0 0 6px rgba(201,151,58,0.7); }
      .pv-rg-rejected { background: #8b1a1a; box-shadow: 0 0 6px rgba(139,26,26,0.7); }
      .pv-ribbon-text { font-family: 'Cinzel', serif; font-size: 8.5px; font-weight: 700; letter-spacing: 2.5px; text-transform: uppercase; color: var(--gold-dim); }

      /* Temple seal at bottom of sanctum */
      .pv-temple-seal {
        position: relative; width: 52px; height: 52px;
        display: flex; align-items: center; justify-content: center;
        margin-top: 18px; z-index: 2;
      }
      .pv-seal-ring {
        position: absolute; inset: 0; border-radius: 50%;
        border: 1px solid rgba(232,196,106,0.2);
        background: radial-gradient(circle, rgba(232,196,106,0.05) 0%, transparent 70%);
        animation: haloBreath 4s ease-in-out infinite alternate;
      }
      .pv-seal-om {
        font-family: 'Cinzel Decorative', cursive; font-size: 26px;
        background: linear-gradient(180deg, #fff8e7 0%, var(--gold-bright) 50%, var(--gold-mid) 100%);
        -webkit-background-clip: text; -webkit-text-fill-color: transparent;
        filter: drop-shadow(0 2px 8px rgba(232,196,106,0.45));
        opacity: 0.65; position: relative; z-index: 1;
      }

      /* ══ DECORATIVE PILLAR (center) ══ */
      .pv-pillar {
        display: flex; flex-direction: column; align-items: center;
        background: linear-gradient(180deg, #1a0404 0%, #2c0a0a 50%, #1a0404 100%);
        position: relative;
      }
      .pv-pillar-cap {
        height: 20px; width: 100%;
        background: linear-gradient(90deg, rgba(201,151,58,0.08), rgba(201,151,58,0.22), rgba(201,151,58,0.08));
        flex-shrink: 0;
      }
      .pv-pillar-shaft {
        flex: 1; width: 100%; position: relative;
        background: linear-gradient(180deg, rgba(201,151,58,0.03), rgba(201,151,58,0.07) 50%, rgba(201,151,58,0.03));
        border-left: 1px solid rgba(201,151,58,0.15);
        border-right: 1px solid rgba(201,151,58,0.15);
      }
      .pv-pillar-rune {
        position: absolute; left: 50%; transform: translateX(-50%);
        font-size: 9px; color: rgba(201,151,58,0.2);
        animation: gentlePulse 3s ease-in-out infinite alternate;
      }

      /* ══ SCROLL (right parchment panel) ══ */
      .pv-scroll {
        background:
          radial-gradient(ellipse at 90% 10%, rgba(201,151,58,0.06) 0%, transparent 50%),
          linear-gradient(155deg, #fdf8f0 0%, #fdf3e3 55%, #f9ead5 100%);
        padding: 42px 44px 40px 44px;
        display: flex; flex-direction: column; gap: 0;
        position: relative;
      }
      .pv-scroll::before {
        content: '';
        position: absolute; top: 0; left: 0; right: 0; bottom: 0;
        background: repeating-linear-gradient(0deg, transparent 0px, transparent 27px, rgba(201,151,58,0.025) 27px, rgba(201,151,58,0.025) 28px);
        pointer-events: none;
      }

      /* Scroll header */
      .pv-scroll-header { margin-bottom: 28px; position: relative; z-index: 1; }
      .pv-scroll-eyebrow {
        display: flex; align-items: center; gap: 10px;
        margin-bottom: 8px;
      }
      .pv-ey-line {
        flex: 1; height: 1px;
        background: linear-gradient(90deg, transparent, rgba(201,151,58,0.35));
      }
      .pv-ey-line:last-child { background: linear-gradient(90deg, rgba(201,151,58,0.35), transparent); }
      .pv-ey-text {
        font-family: 'Cinzel', serif; font-size: 8px; font-weight: 700;
        letter-spacing: 3px; text-transform: uppercase; color: var(--gold-dim);
        white-space: nowrap; flex-shrink: 0;
      }
      .pv-scroll-name {
        font-family: 'Cinzel Decorative', cursive; font-size: 28px; font-weight: 900;
        background: linear-gradient(135deg, #3d0c0c 0%, #5a1010 40%, var(--crimson-deep) 100%);
        -webkit-background-clip: text; -webkit-text-fill-color: transparent;
        line-height: 1.25; margin-bottom: 18px; letter-spacing: -0.3px;
      }
      .pv-scroll-header-rule {
        height: 2px;
        background: linear-gradient(90deg, rgba(201,151,58,0.6) 0%, rgba(201,151,58,0.3) 50%, transparent 100%);
        position: relative;
      }
      .pv-scroll-header-rule::before {
        content: ''; position: absolute; top: 2px; left: 0; right: 30%;
        height: 1px;
        background: linear-gradient(90deg, rgba(201,151,58,0.2), transparent);
      }

      /* Info cards grid */
      .pv-cards-grid {
        display: grid; grid-template-columns: 1fr 1fr;
        gap: 10px; margin-bottom: 16px; position: relative; z-index: 1;
      }
      .pv-card {
        position: relative;
        display: flex; align-items: flex-start; gap: 12px;
        padding: 16px 18px 16px 16px;
        background: rgba(255,251,243,0.8);
        border: 1px solid rgba(201,151,58,0.18);
        overflow: hidden;
        transition: border-color 0.25s, background 0.25s, transform 0.25s, box-shadow 0.25s;
        animation: cardReveal 0.5s cubic-bezier(0.23,1,0.32,1) both;
      }
      @keyframes cardReveal {
        from { opacity: 0; transform: translateY(10px); }
        to   { opacity: 1; transform: translateY(0); }
      }
      .pv-card:hover {
        border-color: rgba(201,151,58,0.45);
        background: rgba(255,253,246,0.97);
        transform: translateY(-2px);
        box-shadow: 0 8px 28px rgba(201,151,58,0.12);
      }
      .pv-card-accent {
        position: absolute; left: 0; top: 0; bottom: 0; width: 3px;
        background: linear-gradient(180deg, transparent, var(--gold-mid) 30%, var(--gold-bright) 60%, var(--gold-mid) 80%, transparent);
        opacity: 0.7;
      }
      .pv-card-corner {
        position: absolute; width: 9px; height: 9px;
        border-color: rgba(201,151,58,0.35); border-style: solid;
      }
      .pv-card-corner.tl { top: 5px; left: 5px; border-width: 1px 0 0 1px; }
      .pv-card-corner.br { bottom: 5px; right: 5px; border-width: 0 1px 1px 0; }
      .pv-card-icon-col { flex-shrink: 0; margin-top: 2px; }
      .pv-card-icon {
        width: 32px; height: 32px; border-radius: 50%;
        background: rgba(201,151,58,0.10);
        border: 1px solid rgba(201,151,58,0.22);
        display: flex; align-items: center; justify-content: center;
        color: var(--gold-mid); font-size: 12px;
        transition: background 0.2s, border-color 0.2s;
      }
      .pv-card:hover .pv-card-icon {
        background: rgba(201,151,58,0.18);
        border-color: rgba(201,151,58,0.4);
      }
      .pv-card-content { display: flex; flex-direction: column; gap: 4px; min-width: 0; flex: 1; }
      .pv-card-label {
        font-family: 'Cinzel', serif; font-size: 8px; font-weight: 700;
        letter-spacing: 2.5px; text-transform: uppercase; color: var(--gold-dim);
      }
      .pv-card-value {
        font-family: 'EB Garamond', serif; font-size: 15px;
        color: var(--text-dark); line-height: 1.45; word-break: break-word;
      }
      .pv-card-value.mono {
        font-family: 'Cinzel', serif; font-size: 13px; letter-spacing: 0.5px;
      }

      /* ID Proof banner */
      .pv-id-banner {
        display: flex; align-items: center; gap: 14px;
        padding: 12px 20px;
        border: 1px solid rgba(201,151,58,0.22);
        background: rgba(255,249,240,0.5);
        cursor: pointer; width: 100%; text-align: left;
        margin-bottom: 16px; position: relative; z-index: 1;
        transition: background 0.2s, border-color 0.2s, transform 0.2s;
      }
      .pv-id-banner:hover { background: rgba(255,252,242,0.9); border-color: rgba(201,151,58,0.5); transform: translateY(-1px); }
      .pv-id-banner-icon { color: var(--gold-mid); font-size: 16px; flex-shrink: 0; }
      .pv-id-banner-label {
        font-family: 'Cinzel', serif; font-size: 9px; font-weight: 700;
        letter-spacing: 2px; text-transform: uppercase; color: var(--gold-dim); flex: 1;
      }
      .pv-id-banner-action {
        font-family: 'Cinzel', serif; font-size: 9.5px; font-weight: 700;
        letter-spacing: 1px; text-transform: uppercase; color: var(--gold-mid);
        transition: color 0.2s;
      }
      .pv-id-banner:hover .pv-id-banner-action { color: var(--crimson-deep); }

      /* Edit CTA button */
      .pv-edit-cta {
        position: relative; z-index: 1;
        display: flex; align-items: center; justify-content: center; gap: 12px;
        padding: 16px 32px; border: none; cursor: pointer;
        background: linear-gradient(135deg,
          #6b4209 0%, #9a6515 10%, var(--gold-mid) 25%,
          var(--gold-bright) 40%, #fdf0c8 52%,
          var(--gold-bright) 64%, var(--gold-mid) 78%,
          #9a6515 90%, #6b4209 100%
        );
        overflow: hidden; margin-top: 4px;
        box-shadow: 0 6px 28px rgba(201,151,58,0.35), inset 0 1px 0 rgba(255,255,255,0.3), inset 0 -1px 0 rgba(0,0,0,0.1);
        clip-path: polygon(16px 0%, calc(100% - 16px) 0%, 100% 50%, calc(100% - 16px) 100%, 16px 100%, 0% 50%);
        transition: transform 0.3s ease, box-shadow 0.3s ease, filter 0.3s ease;
      }
      .pv-edit-cta:hover {
        transform: translateY(-3px);
        box-shadow: 0 14px 40px rgba(201,151,58,0.55), inset 0 1px 0 rgba(255,255,255,0.3);
        filter: brightness(1.06);
      }
      .pv-edit-cta:active { transform: translateY(-1px); }
      .pv-cta-shimmer {
        position: absolute; top: -50%; left: -70%; width: 45%; height: 200%;
        background: rgba(255,255,255,0.22); transform: skewX(-20deg);
        transition: left 0.6s ease; pointer-events: none;
      }
      .pv-edit-cta:hover .pv-cta-shimmer { left: 130%; }
      .pv-cta-icon { font-size: 13px; color: var(--crimson-deep); position: relative; z-index: 1; }
      .pv-cta-text {
        font-family: 'Cinzel', serif; font-size: 10.5px; font-weight: 700;
        letter-spacing: 3px; text-transform: uppercase; color: var(--crimson-deep);
        position: relative; z-index: 1;
      }
      .pv-cta-arrow {
        font-size: 14px; color: var(--crimson-deep);
        position: relative; z-index: 1;
        transition: transform 0.25s;
      }
      .pv-edit-cta:hover .pv-cta-arrow { transform: translateX(4px); }

      /* ── FOOTER BAR ── */
      .pv-footer-bar {
        background: linear-gradient(135deg, #1a0404 0%, #2c0a0a 40%, #3d1010 60%, #2c0a0a 80%, #1a0404 100%);
        border-top: 1px solid rgba(201,151,58,0.2);
        padding: 10px 32px;
      }
      .pv-footer-inner { display: flex; align-items: center; justify-content: center; }
      .pv-footer-text {
        font-family: 'Cinzel', serif; font-size: 8.5px; font-weight: 600;
        letter-spacing: 3px; text-transform: uppercase;
        color: rgba(232,196,106,0.4);
      }

      /* ── RESPONSIVE ── */
      @media (max-width: 860px) {
        .pv-body { grid-template-columns: 1fr; }
        .pv-pillar { display: none; }
        .pv-sanctum { padding: 44px 28px 36px; }
        .pv-sanctum::after { display: none; }
        .pv-scroll { padding: 32px 24px; }
        .pv-cards-grid { grid-template-columns: 1fr; }
        .pv-scroll-name { font-size: 22px; }
      }
      @media (max-width: 480px) {
        .pv-scroll { padding: 24px 18px; }
        .pv-scroll-name { font-size: 18px; }
        .pv-devotee-name { font-size: 14px; }
        .pv-crown-inner { padding: 10px 16px; }
      }
      /* ══════════════════════════════════════════════════
         ID PROOF MODAL
      ══════════════════════════════════════════════════ */
      .idm-overlay {
        position: fixed; inset: 0; z-index: 3000;
        background: rgba(10, 2, 2, 0.82);
        backdrop-filter: blur(6px);
        display: flex; align-items: center; justify-content: center;
        padding: 24px;
        animation: idmOverlayIn 0.25s ease both;
      }
      @keyframes idmOverlayIn {
        from { opacity: 0; }
        to   { opacity: 1; }
      }

      .idm-dialog {
        width: 100%; max-width: 700px;
        background: linear-gradient(160deg, #fdf8f0 0%, #fdf3e3 55%, #f9ead5 100%);
        border: 1px solid rgba(201,151,58,0.4);
        box-shadow:
          0 0 0 1px rgba(201,151,58,0.12),
          0 0 0 5px rgba(201,151,58,0.05),
          0 40px 100px rgba(10,2,2,0.55);
        overflow: hidden;
        display: flex; flex-direction: column;
        max-height: 88vh;
        animation: idmDialogIn 0.35s cubic-bezier(0.23,1,0.32,1) both;
        position: relative;
      }
      @keyframes idmDialogIn {
        from { opacity: 0; transform: scale(0.93) translateY(20px); }
        to   { opacity: 1; transform: scale(1) translateY(0); }
      }
      .idm-dialog::before {
        content: '';
        position: absolute; top: 0; left: 0; right: 0; height: 3px;
        background: linear-gradient(90deg, transparent, var(--gold-dim) 10%, var(--gold-bright) 30%, #fff8e7 50%, var(--gold-bright) 70%, var(--gold-dim) 90%, transparent);
        z-index: 1;
      }

      /* Header */
      .idm-header {
        display: flex; align-items: center; justify-content: space-between;
        padding: 22px 28px 18px;
      }
      .idm-header-left { display: flex; align-items: center; gap: 16px; }
      .idm-header-icon {
        width: 42px; height: 42px; border-radius: 50%; flex-shrink: 0;
        background: rgba(201,151,58,0.10);
        border: 1px solid rgba(201,151,58,0.28);
        display: flex; align-items: center; justify-content: center;
        color: var(--gold-mid); font-size: 17px;
      }
      .idm-eyebrow {
        display: block;
        font-family: 'Cinzel', serif; font-size: 8px; font-weight: 700;
        letter-spacing: 3px; text-transform: uppercase; color: var(--gold-dim);
        margin-bottom: 4px;
      }
      .idm-title {
        font-family: 'Cinzel Decorative', cursive; font-size: 15px; font-weight: 900;
        background: linear-gradient(135deg, #3d0c0c, var(--crimson-deep));
        -webkit-background-clip: text; -webkit-text-fill-color: transparent;
      }
      .idm-close {
        width: 36px; height: 36px; border-radius: 50%;
        background: rgba(201,151,58,0.07);
        border: 1px solid rgba(201,151,58,0.22);
        display: flex; align-items: center; justify-content: center;
        color: var(--gold-dim); font-size: 13px; cursor: pointer;
        transition: background 0.2s, color 0.2s, border-color 0.2s, transform 0.2s;
        flex-shrink: 0;
      }
      .idm-close:hover {
        background: rgba(201,151,58,0.16);
        border-color: rgba(201,151,58,0.5);
        color: var(--text-dark);
        transform: rotate(90deg);
      }

      .idm-rule {
        height: 1px; margin: 0 28px;
        background: linear-gradient(90deg, rgba(201,151,58,0.5), rgba(201,151,58,0.2), transparent);
      }

      /* Body — image/pdf */
      .idm-body {
        flex: 1; overflow: auto;
        padding: 20px 28px;
        display: flex; align-items: center; justify-content: center;
        min-height: 200px;
        background: rgba(255,252,244,0.5);
      }
      .idm-img {
        max-width: 100%; max-height: 60vh;
        border: 1px solid rgba(201,151,58,0.2);
        box-shadow: 0 8px 40px rgba(26,4,4,0.18);
        display: block;
      }
      .idm-iframe {
        width: 100%; height: 60vh; border: 1px solid rgba(201,151,58,0.2);
        box-shadow: 0 8px 40px rgba(26,4,4,0.18);
      }

      /* Footer */
      .idm-footer {
        padding: 16px 28px 22px;
        display: flex; align-items: center; justify-content: flex-end; gap: 12px;
        border-top: 1px solid rgba(201,151,58,0.12);
      }
      .idm-open-btn {
        font-family: 'Cinzel', serif; font-size: 9.5px; font-weight: 700;
        letter-spacing: 2px; text-transform: uppercase;
        color: var(--gold-mid); text-decoration: none;
        padding: 10px 20px;
        border: 1px solid rgba(201,151,58,0.3);
        background: rgba(201,151,58,0.06);
        clip-path: polygon(10px 0%, calc(100% - 10px) 0%, 100% 50%, calc(100% - 10px) 100%, 10px 100%, 0% 50%);
        transition: background 0.2s, border-color 0.2s, color 0.2s;
        display: inline-flex; align-items: center;
      }
      .idm-open-btn:hover {
        background: rgba(201,151,58,0.14); border-color: rgba(201,151,58,0.55);
        color: var(--text-dark);
      }
      .idm-cancel-btn {
        font-family: 'Cinzel', serif; font-size: 9.5px; font-weight: 700;
        letter-spacing: 2px; text-transform: uppercase;
        color: rgba(74,26,8,0.55);
        padding: 10px 24px;
        background: linear-gradient(135deg, #6b4209 0%, var(--gold-mid) 30%, var(--gold-bright) 50%, var(--gold-mid) 70%, #6b4209 100%);
        border: none; cursor: pointer;
        clip-path: polygon(10px 0%, calc(100% - 10px) 0%, 100% 50%, calc(100% - 10px) 100%, 10px 100%, 0% 50%);
        color: var(--crimson-deep);
        box-shadow: 0 4px 16px rgba(201,151,58,0.3);
        transition: filter 0.2s, transform 0.2s, box-shadow 0.2s;
      }
      .idm-cancel-btn:hover {
        filter: brightness(1.07); transform: translateY(-1px);
        box-shadow: 0 8px 24px rgba(201,151,58,0.45);
      }

      @media (max-width: 520px) {
        .idm-dialog { max-height: 95vh; }
        .idm-header { padding: 18px 18px 14px; }
        .idm-body { padding: 14px 18px; }
        .idm-footer { padding: 14px 18px 18px; }
        .idm-rule { margin: 0 18px; }
      }
    `}</style>
  );
}